import { useNavigate } from 'react-router';
import { SettingsIcon } from 'lucide-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useAuth, usePermissions } from '@/entities/user';
import {
  saveWorkspaceForNextLoad,
  useCurrentWorkspaceId,
  type Workspace,
} from '@/entities/workspace';
import { getChatActions } from '@/lib/chat-actions/chat-actions-bindings';
import { ENV } from '@/lib/config/env';
import { McpLogo } from '@/shared/ui';
import { reset, track } from '@/shared/analytics';
import { ROUTES } from '@/shared/config/routes';
import { useUserOrganizations } from '@/widgets/app-shell/model/use-user-organizations';
import { SidebarRail, SidebarRailSkeleton } from '../SidebarRail';
import { useTranslation } from 'react-i18next';

function logout() {
  track('user_signed_out', {});
  reset();
  window.location.assign(`${ENV.PLATFORM_API_BASE_URL}/auth/logout`);
}

export const AppSidebar = () => {
  const { t } = useTranslation('common');
  const mcpServersEnabled = useBooleanFlagValue('user_managed_mcp_servers', false);
  const connectedAppsEnabled = useBooleanFlagValue('connected_apps', false);
  const { value: validatedUser, fetching, error } = useAuth((s) => s.signedInUser);
  const selectedWorkspaceId = useCurrentWorkspaceId();
  const navigate = useNavigate();
  // `useIsChatBusy` comes from the chat-actions binding seam — keeps the
  // `widgets/app-shell` layer from value-importing `@/features/chats`
  // (FSD: widgets MUST NOT cross-import features). Bound at boot in
  // `main.tsx` from `@/features/chats`'s `useIsAiBusy`.
  const isChatBusy = getChatActions().useIsChatBusy();
  const { canViewStudioSettings } = usePermissions();
  const { hasMultipleOrganizations, assignedOrganization } = useUserOrganizations();

  if (fetching || !selectedWorkspaceId) {
    return <SidebarRailSkeleton />;
  }

  if (error || !validatedUser) {
    return null;
  }

  const mcpMenuItems = mcpServersEnabled
    ? [
        {
          id: 'my-mcps',
          label: t('appShell.appSidebar.myMcps'),
          icon: <McpLogo />,
          onClick: () => {
            void navigate(ROUTES.MY_MCPS);
          },
        },
      ]
    : [];

  // One in-app entry stands for both the old "Studio settings" row and the platform
  // "Admin settings" link, which is why `showAdminSettings` below is always off.
  // `canViewStudioSettings` already covers every gate used inside /admin, workspace
  // admins included. Connected apps is the exception: it lists the user's own grants,
  // so with its flag on everyone needs the way in.
  const userMenuItems = [
    ...mcpMenuItems,
    ...(canViewStudioSettings || connectedAppsEnabled
      ? [
          {
            id: 'organization-settings',
            label: t('appShell.sidebarUserPopover.organizationSettings'),
            icon: <SettingsIcon className="size-md" />,
            onClick: () => {
              void navigate(ROUTES.ADMIN);
            },
          },
        ]
      : []),
  ];

  const workspaceMenuHeaderAction = hasMultipleOrganizations
    ? {
        // Land on Home, not the current URL: org-scoped resources (e.g. a
        // project) don't exist in the new org, so bouncing back to them only
        // triggers the deep-link resolution prompt for the org just left.
        href: `${ENV.PLATFORM_BASE_URL}/switch-organization?redirectUri=${encodeURIComponent(window.location.origin)}`,
        // eslint-disable-next-line i18next/no-literal-string -- anchor target attribute value
        target: '_self' as const,
      }
    : undefined;

  const handleWorkspaceChange = (ws: Workspace) => {
    if (isChatBusy) return;
    const orgId = validatedUser.organization.id;
    // Single hard navigation to home, which boots from storage. An SPA navigate()
    // beforehand would flash the home page with the old workspace's data before
    // the reload takes over.
    saveWorkspaceForNextLoad(ws.id, orgId);
    window.location.assign(ROUTES.HOME);
  };

  return (
    <SidebarRail
      user={validatedUser}
      workspaces={validatedUser.workspaces}
      selectedWorkspaceId={selectedWorkspaceId}
      userMenuItems={userMenuItems}
      organizationLogoUrl={assignedOrganization?.logoUrl}
      organizationName={assignedOrganization?.name}
      workspaceMenuHeaderAction={workspaceMenuHeaderAction}
      onWorkspaceChange={handleWorkspaceChange}
      onSignOutClick={logout}
      platformAppUrl={ENV.PLATFORM_BASE_URL || ''}
      isChatBusy={isChatBusy}
      showAdminSettings={false}
    />
  );
};

/**
 * Exported through the `widgets/app-shell` barrel so consumers (e.g. route
 * loaders or Suspense fallbacks) can render the same loading shell that
 * `<AppSidebar />` shows internally during auth.
 */
export const AppSidebarSkeleton = () => <SidebarRailSkeleton />;
